import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Chip,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { Form as AntForm, Input, InputNumber, Button, Select as AntSelect, Spin, Tabs as AntTabs } from 'antd';
import axios from 'axios';
import { useAuthHeader, useAuthUser } from '../../Services/Auth/sessionAuth';
import { encryptBody, getMethodLabel, mfaApi, MFA_METHOD, normalizeStatusCode, normalizeToUpperKeys, normalizeListToUpperKeys, extractRows, parseEncryptedResponse, unwrapMfaPayload, logMfaRequest, logMfaSuccess, logMfaError } from './mfaApi';
import type { MfaActionMode } from './MfaPage';
import Swal from 'sweetalert2';
import MfaPasswordRotationPanel from './MfaPasswordRotationPanel';

// ---------------------------------------------------------------------------
// Types
//
// Convention de casse : MAJUSCULES pour tous les DTO "listes" (Utilisateur,
// Groupe, MfaUser) car ce sont les noms de colonnes SQL bruts renvoyés par
// les procédures stockées (sp_Utilisateur_GetAll, sp_Groupe_GetAll,
// sp_UserMfaSetting_GetAllWithLevel). Les listes sont de toute façon passées
// dans normalizeListToUpperKeys avant setState, donc peu importe si le
// backend renvoie PascalCase ou ALLCAPS — la casse est uniformisée ici.
// ---------------------------------------------------------------------------

interface SecurityPolicy {
  OtpLength: number; PasswordMinLength: number; PasswordMaxLength: number;
  OtpLifetime: number; OtpMaxAttempts: number; PasswordHistoryCount: number; TempPasswordLifetime: number;
  Statut: string; ModifiedBy: string;
}

interface MfaSocietyPolicy {
  Id?: number; DefaultMfaMethod?: number; Statut?: string; IsRequired?: boolean; ModifiedBy?: string;
  DateCreation?: string | Date; DateModification?: string | Date;
}

interface AgenceMfaPolicy {
  Id?: number;
  CodAge?: string;
  MfaMethod?: number;
  Statut?: string;
  DateCreation?: string;
  DateModification?: string;
  CreatedBy?: string;
  ModifiedBy?: string;
}

interface RoleMfaPolicy {
  CodGroup?: string;
  MfaMethod?: number;
  Statut?: string;
  DateCreation?: string;
  DateModification?: string;
  CreatedBy?: string;
  ModifiedBy?: string;
}

interface MfaUser { CODUTI?: string; CODGROUP?: string; MFAMETHOD?: number; STATUT?: string; ISCONFIGURED?: boolean; LASTCONFIGUREDDATE?: string; MFALEVEL?: string; }
interface AgenceOption { CodeAgence: string; Libelle: string; }
interface UtilisateurData { CODUTI: string; NOMUTI: string; CODGROUP: string; CODAGE: string; MFAMETHOD?: number; STATUT?: string; ISCONFIGURED?: boolean; LASTCONFIGUREDDATE?: string; MFALEVEL?: string; }
interface GroupData { CODGROUP: string; LIBELLE: string; }

type MfaLevel = 'global' | 'society' | 'agency' | 'role' | 'user' | 'activated';

const LEVELS: { value: MfaLevel; label: string }[] = [
  { value: 'global', label: 'Sécurité globale' },
  { value: 'society', label: 'Société' },
  { value: 'agency', label: 'Agence' },
  { value: 'role', label: 'Groupe / Rôle' },
  { value: 'user', label: 'Utilisateur' },
];

// Options des listes déroulantes (style charte : antd Select)
const METHOD_OPTIONS = [
  { value: MFA_METHOD.none, label: 'Aucun MFA' },
  { value: MFA_METHOD.emailOtp, label: 'Email OTP' },
  { value: MFA_METHOD.smsOtp, label: 'SMS OTP' },
  { value: MFA_METHOD.totp, label: 'TOTP' },
];
const METHOD_OPTIONS_NO_NONE = METHOD_OPTIONS.filter((o) => o.value !== MFA_METHOD.none);
const STATUT_OPTIONS = [
  { value: 'ACTIF', label: 'Actif' },
  { value: 'INACTIF', label: 'Inactif' },
];
// Style commun des libellés de champs (label à gauche, retour à la ligne autorisé)
const LABEL_COL = { span: 9, style: { whiteSpace: 'pre-wrap' as const } };

const emptyAgencePolicy: AgenceMfaPolicy = {
  MfaMethod: MFA_METHOD.emailOtp,
  Statut: 'ACTIF',
  CreatedBy: 'SYSTEM',
};
const emptyRolePolicy: RoleMfaPolicy = {
  MfaMethod: MFA_METHOD.emailOtp,
  Statut: 'ACTIF',
  CreatedBy: 'SYSTEM',
};
const emptySocietyPolicy: MfaSocietyPolicy = { DefaultMfaMethod: MFA_METHOD.emailOtp, Statut: 'INACTIF', IsRequired: false, ModifiedBy: 'SYSTEM' };

interface MfaAdminPanelProps {
  mode?: MfaActionMode;
  saveRequest?: number;
  onSaved?: () => void;
}

export default function MfaAdminPanel({ mode = 'view', saveRequest = 0, onSaved }: MfaAdminPanelProps) {
  const auth = useAuthUser();
  const authHeader = useAuthHeader();
  const currentUser: any = auth();
  const headers = { Authorization: authHeader(), 'Content-Type': 'application/json' };

  const [level, setLevel] = useState<MfaLevel>('global');
  const [loading, setLoading] = useState(false);

  // ---- Niveau : Sécurité globale ------------------------------------------------
  const [globalPolicy, setGlobalPolicy] = useState<SecurityPolicy>({
    OtpLength: 6, PasswordMinLength: 8, PasswordMaxLength: 64,
    OtpLifetime: 5, OtpMaxAttempts: 5, PasswordHistoryCount: 5, TempPasswordLifetime: 24,
    Statut: 'ACTIF', ModifiedBy: currentUser?.coduti ?? 'SYSTEM',
  });

  // ---- Niveau : Société -----------------------------------------------------
  const [societyPolicy, setSocietyPolicy] = useState<MfaSocietyPolicy>(emptySocietyPolicy);

  // ---- Niveau : Agence --------------------------------------------------------
  const [agences, setAgences] = useState<AgenceOption[]>([]);
  const [agencePolicies, setAgencePolicies] = useState<AgenceMfaPolicy[]>([]);
  const [selectedAgence, setSelectedAgence] = useState('');
  const [currentAgencePolicy, setCurrentAgencePolicy] = useState<AgenceMfaPolicy>(emptyAgencePolicy);

  // ---- Niveau : Groupe / Rôle -------------------------------------------------
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [rolePolicies, setRolePolicies] = useState<RoleMfaPolicy[]>([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [currentRolePolicy, setCurrentRolePolicy] = useState<RoleMfaPolicy>(emptyRolePolicy);

  // ---- Niveau : Utilisateur ----------------------------------------------------
  const [utilisateurs, setUtilisateurs] = useState<UtilisateurData[]>([]);
  const [configuredUsers, setConfiguredUsers] = useState<MfaUser[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [userMethod, setUserMethod] = useState<number>(MFA_METHOD.totp);
  const [tempPassword, setTempPassword] = useState('');
  const [selectedUserTemp, setSelectedUserTemp] = useState<{ ExpiresAt?: string; Statut?: string } | null>(null);
  const [activatedSearch, setActivatedSearch] = useState('');
  const formLocked = mode === 'view';

  // ---------------------------------------------------------------------------
  // Chargement
  // ---------------------------------------------------------------------------

  async function loadGlobalPolicy() {
    try {
      logMfaRequest('loadGlobalPolicy', mfaApi.securityPolicyConfig);
      const res = await axios.get(mfaApi.securityPolicyConfig, { headers, withCredentials: true });
      const payload = logMfaSuccess('loadGlobalPolicy', res);
      setGlobalPolicy((current) => ({ ...current, ...(payload?.Result ?? payload ?? {}) }));
    } catch (e) { logMfaError('loadGlobalPolicy', e); }
  }

  async function loadSocietyPolicy() {
    try {
      logMfaRequest('loadSocietyPolicy', mfaApi.mfaPolicyAll);
      const res = await axios.get(mfaApi.mfaPolicyAll, { headers, withCredentials: true });
      const parsed = logMfaSuccess('loadSocietyPolicy', res);
      const list = parsed?.Result ?? parsed?.result ?? [];
      if (Array.isArray(list) && list.length > 0) setSocietyPolicy(list[0]);
      else console.warn('', list);
    } catch (e) { logMfaError('loadSocietyPolicy', e); }
  }

  async function handleResetUserMfa(codUti: string) {
  const confirm = await Swal.fire({
    icon: 'warning',
    title: 'Réinitialiser le MFA',
    text: `Désactiver le MFA de ${codUti} ?`,
    showCancelButton: true,
    confirmButtonColor: '#0d4a70',
    confirmButtonText: 'Réinitialiser',
    cancelButtonText: 'Annuler',
  });
  if (!confirm.isConfirmed) return;

  setLoading(true);
  try {
    const body = { codUti }; // le contrôleur lit un objet dynamique, clé en minuscule
    logMfaRequest('handleResetUserMfa', mfaApi.userMfaReset, body);
    const res = await axios.post(mfaApi.userMfaReset, encryptBody(body), { headers, withCredentials: true });
    const parsed = logMfaSuccess('handleResetUserMfa', res);
    if (normalizeStatusCode(parsed) < 300) {
      notifySuccess('MFA réinitialisé pour cet utilisateur.');
      loadConfiguredUsers();
    } else {
      notifyError(parsed?.Message ?? 'Erreur lors de la réinitialisation.');
    }
  } catch (e: any) {
    notifyError(logMfaError('handleResetUserMfa', e)?.Message ?? 'Le serveur a refusé la transaction.');
  } finally { setLoading(false); }
}

  async function loadAgences() {
    try {
      logMfaRequest('loadAgences', mfaApi.agencesAllBranch);
      const res = await axios.get(mfaApi.agencesAllBranch, { headers, withCredentials: true });
      const parsed = logMfaSuccess('loadAgences', res);
      const result = parsed?.Result ?? parsed?.result ?? [];
      setAgences(Array.isArray(result) ? result : []);
    } catch (e) { logMfaError('loadAgences', ''); }
  }

  async function loadAgencePolicies() {
    try {
      logMfaRequest('loadAgencePolicies', mfaApi.agenceMfaPolicyAll);
      const res = await axios.get(mfaApi.agenceMfaPolicyAll, { headers, withCredentials: true });
      const parsed = logMfaSuccess('loadAgencePolicies', res);
      const result = parsed?.Result ?? parsed?.result ?? [];
      setAgencePolicies(Array.isArray(result) ? result : []);
    } catch (e) { logMfaError('loadAgencePolicies', e); }
  }

  async function loadGroups() {
    try {
      logMfaRequest('loadGroups', mfaApi.groupesAll);
      const res = await axios.get(mfaApi.groupesAll, { headers, withCredentials: true });

      // Diagnostic brut AVANT tout déchiffrement : permet de distinguer un
      // souci de transport HTTP (URL, 404, CORS) d'un souci de parsing

      const parsed = logMfaSuccess('loadGroups', res);


      const rawResult = parsed?.Result ?? parsed?.result ?? [];
      const rows = extractRows(rawResult);


      // Normalisation : le backend (sp_Groupe_GetAll) renvoie des colonnes SQL
      // (CODGROUP, LIBELLE) ou un DTO PascalCase selon le helper d'exécution —
      // dans les deux cas on uniformise en MAJUSCULES pour matcher GroupData.
      // normalizeListToUpperKeys déballe aussi {Table:[...]} en interne.
      const normalized = normalizeListToUpperKeys<GroupData>(rawResult);


      setGroups(normalized);
    } catch (e) {
      logMfaError('loadGroups', e);
    } finally {
      console.groupEnd();
    }
  }

  async function loadRolePolicies() {
    try {
      logMfaRequest('loadRolePolicies', mfaApi.roleMfaPolicyAll);
      const res = await axios.get(mfaApi.roleMfaPolicyAll, { headers, withCredentials: true });
      const parsed = logMfaSuccess('loadRolePolicies', res);
      const result = parsed?.Result ?? parsed?.result ?? [];
      setRolePolicies(Array.isArray(result) ? result : []);
    } catch (e) { logMfaError('loadRolePolicies', e); }
  }

  async function loadUtilisateurs() {
    try {
      logMfaRequest('loadUtilisateurs', mfaApi.mfaUsersAll);
      const res = await axios.get(mfaApi.mfaUsersAll, { headers, withCredentials: true });

      // Diagnostic brut AVANT tout parsing/déchiffrement : si ça casse plus
      // loin, on veut savoir si le problème vient du transport HTTP ou du
      // déchiffrement du corps de réponse.

      const parsed = logMfaSuccess('loadUtilisateurs', res);

      const rawResult = parsed?.Result ?? parsed?.result ?? [];
      const rows = extractRows(rawResult);
      // Normalisation : sp_Utilisateur_GetAll renvoie CODUTI/NOMUTI/CODGROUP/CODAGE
      // en colonnes SQL brutes, imbriquées sous Result.Table (sérialisation
      // DataTable côté back) — normalizeListToUpperKeys déballe et uniformise.
      const normalized = normalizeListToUpperKeys<UtilisateurData>(rawResult);

      setUtilisateurs(normalized);
    } catch (e) {
      logMfaError('loadUtilisateurs', e);
    } finally {
      console.groupEnd();
    }
  }

  async function loadConfiguredUsers() {
    try {
      logMfaRequest('loadConfiguredUsers', mfaApi.mfaUsers);
      const res = await axios.get(mfaApi.mfaUsers, { headers, withCredentials: true });
      const parsed = logMfaSuccess('loadConfiguredUsers', res);
      const rawResult = parsed?.Result ?? parsed ?? [];
      // Même remarque que loadUtilisateurs : le backend renvoie souvent
      // Result: { Table: [...] } (sérialisation DataTable) plutôt qu'un
      // tableau direct — normalizeListToUpperKeys déballe les deux formes.
      const normalized = normalizeListToUpperKeys<MfaUser>(rawResult);
      const filtered = normalized.filter((u) => Boolean(u.ISCONFIGURED));
      setConfiguredUsers(filtered);
    } catch (e) { logMfaError('loadConfiguredUsers', ''); }
  }

  async function loadAll() {
    setLoading(true);
    try {
      await Promise.all([
        loadGlobalPolicy(),
        loadSocietyPolicy(),
        loadAgences(),
        loadAgencePolicies(),
        loadGroups(),
        loadRolePolicies(),
        loadUtilisateurs(),
        loadConfiguredUsers(),
      ]);

    } catch (e) {
      logMfaError('loadAll', '');
      Swal.fire({ icon: 'error', title: 'Erreur', text: 'Données MFA administrateur indisponibles.', confirmButtonColor: '#0d4a70' });
    } finally { setLoading(false); }
  }

  useEffect(() => { loadAll(); }, []);

  // Sélection d'une ligne agence/rôle → recharge le formulaire correspondant
  useEffect(() => {
    if (!selectedAgence) return;
    const existing = agencePolicies.find((p) => p.CodAge === selectedAgence);
    setCurrentAgencePolicy(existing ?? { ...emptyAgencePolicy, CodAge: selectedAgence });
  }, [selectedAgence, agencePolicies]);

  useEffect(() => {
    if (!selectedGroup) return;
    const existing = rolePolicies.find((p) => p.CodGroup === selectedGroup);
    setCurrentRolePolicy(existing ?? { ...emptyRolePolicy, CodGroup: selectedGroup });
  }, [selectedGroup, rolePolicies]);

  
  // Ajouter → réinitialise la sélection du niveau courant pour une nouvelle entrée
  useEffect(() => {
    if (mode !== 'add') return;
    if (level === 'society') setSocietyPolicy(emptySocietyPolicy);
    if (level === 'agency') { setSelectedAgence(''); setCurrentAgencePolicy(emptyAgencePolicy); }
    if (level === 'role') { setSelectedGroup(''); setCurrentRolePolicy(emptyRolePolicy); }
    if (level === 'user') { setSelectedUser(''); setUserMethod(MFA_METHOD.totp); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, level]);

  // Le bouton "Enregistrer" de la barre d'actions globale déclenche la sauvegarde du niveau actif
  useEffect(() => {
    if (saveRequest > 0 && mode !== 'view') handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequest]);

  // ---------------------------------------------------------------------------
  // Sauvegarde
  // ---------------------------------------------------------------------------

  function notifySuccess(text: string) {
    Swal.fire({ icon: 'success', title: 'Succès', text, confirmButtonColor: '#0d4a70' });
  }
  function notifyError(text: string) {
    Swal.fire({ icon: 'error', title: 'Erreur', text, confirmButtonColor: '#0d4a70' });
  }
  function notifyWarning(text: string) {
    Swal.fire({ icon: 'warning', title: 'Attention', text, confirmButtonColor: '#0d4a70' });
  }

  async function handleSave() {
    if (level === 'global') return saveGlobalPolicy();
    if (level === 'society') return saveSocietyPolicy();
    if (level === 'agency') return saveAgencePolicy();
    if (level === 'role') return saveRolePolicy();
    if (level === 'user') return saveUserAssignment();
  }

  async function saveGlobalPolicy() {
    setLoading(true);
    try {
      const payload = {
        OtpLength: Number(globalPolicy.OtpLength),
        PasswordMinLength: Number(globalPolicy.PasswordMinLength),
        PasswordMaxLength: Number(globalPolicy.PasswordMaxLength),
        OtpLifetime: Number(globalPolicy.OtpLifetime),
        OtpMaxAttempts: Number(globalPolicy.OtpMaxAttempts),
        PasswordHistoryCount: Number(globalPolicy.PasswordHistoryCount),
        TempPasswordLifetime: Number(globalPolicy.TempPasswordLifetime),
        Statut: globalPolicy.Statut,
        ModifiedBy: currentUser?.coduti ?? 'SYSTEM',
      };
      logMfaRequest('saveGlobalPolicy', mfaApi.securityPolicyConfig, payload);
      const res = await axios.put(mfaApi.securityPolicyConfig, encryptBody(payload), { headers, withCredentials: true });
      const parsed = logMfaSuccess('saveGlobalPolicy', res);
      if (normalizeStatusCode(parsed) < 300) {
        notifySuccess('Politique de sécurité globale enregistrée avec succès.');
        onSaved?.(); loadGlobalPolicy();
      } else {
        notifyError(parsed?.Message ?? "Erreur lors de l'enregistrement.");
      }
    } catch (e: any) {
      const parsed = logMfaError('saveGlobalPolicy', '');
      notifyError(parsed?.Message ?? "Le serveur a refusé la transaction.");
    } finally { setLoading(false); }
  }

  async function saveSocietyPolicy() {
    setLoading(true);
    try {
      const payload = {
        DefaultMfaMethod: Number(societyPolicy.DefaultMfaMethod ?? MFA_METHOD.emailOtp),
        Statut: societyPolicy.Statut ?? 'ACTIF',
        IsRequired: Boolean(societyPolicy.IsRequired),
        ModifiedBy: currentUser?.coduti ?? 'SYSTEM',
      };
      const isUpdate = Boolean(societyPolicy.DateCreation);
      logMfaRequest('saveSocietyPolicy', mfaApi.mfaPolicy, { method: isUpdate ? 'put' : 'post', payload });
      const res = await axios({
        method: isUpdate ? 'put' : 'post',
        url: mfaApi.mfaPolicy,
        data: encryptBody(payload),
        headers, withCredentials: true,
      });
      const parsed = logMfaSuccess('saveSocietyPolicy', res);
      if (normalizeStatusCode(parsed) < 300) {
        notifySuccess('Politique MFA société enregistrée avec succès.');
        onSaved?.(); loadSocietyPolicy();
      } else {
        notifyError(parsed?.Message ?? "Erreur lors de l'enregistrement.");
      }
    } catch (e: any) {
      const parsed = logMfaError('saveSocietyPolicy', '');
      notifyError(parsed?.Message ?? "Erreur lors de l'enregistrement de la politique MFA société.");
    } finally { setLoading(false); }
  }

  async function saveAgencePolicy() {
    if (!selectedAgence) { notifyWarning('Veuillez sélectionner une agence.'); return; }
    setLoading(true);
    try {
      const existing = agencePolicies.find((p) => p.CodAge === selectedAgence);
      const isUpdate = !!existing;

      const payload = {
        CodAge: selectedAgence,
        MfaMethod: Number(currentAgencePolicy.MfaMethod),
        Statut: currentAgencePolicy.Statut ?? 'ACTIF',
        CreatedBy: currentUser?.coduti ?? 'SYSTEM',
        ModifiedBy: currentUser?.coduti ?? 'SYSTEM',
      };

      logMfaRequest('saveAgencePolicy', mfaApi.agenceMfaPolicy, { method: isUpdate ? 'put' : 'post', payload });
      const res = await axios({
        method: isUpdate ? 'put' : 'post',
        url: mfaApi.agenceMfaPolicy,
        data: encryptBody(payload),
        headers, withCredentials: true,
      });
      const parsed = logMfaSuccess('saveAgencePolicy', res);
      if (normalizeStatusCode(parsed) < 300) {
        notifySuccess(isUpdate ? 'Politique MFA agence mise à jour avec succès.' : 'Politique MFA agence créée avec succès.');
        onSaved?.(); loadAgencePolicies();
      } else {
        notifyError(parsed?.Message ?? "Erreur lors de l'enregistrement de la politique MFA agence.");
      }
    } catch (e: any) {
      const errorMsg = logMfaError('saveAgencePolicy', '')?.Message ?? e?.message ?? "Erreur lors de l'enregistrement de la politique MFA agence.";
      notifyError(errorMsg);
    } finally { setLoading(false); }
  }

  async function saveRolePolicy() {
    if (!selectedGroup) { notifyWarning('Veuillez sélectionner un groupe.'); return; }
    setLoading(true);
    try {
      const existing = rolePolicies.find((p) => p.CodGroup === selectedGroup);
      const isUpdate = !!existing;

      const payload = {
        CodGroup: selectedGroup,
        MfaMethod: Number(currentRolePolicy.MfaMethod),
        Statut: currentRolePolicy.Statut ?? 'ACTIF',
        CreatedBy: currentUser?.coduti ?? 'SYSTEM',
        ModifiedBy: currentUser?.coduti ?? 'SYSTEM',
      };

      logMfaRequest('saveRolePolicy', mfaApi.roleMfaPolicy, { method: isUpdate ? 'put' : 'post', payload });
      const res = await axios({
        method: isUpdate ? 'put' : 'post',
        url: mfaApi.roleMfaPolicy,
        data: encryptBody(payload),
        headers, withCredentials: true,
      });
      const parsed = logMfaSuccess('saveRolePolicy', res);
      if (normalizeStatusCode(parsed) < 300) {
        notifySuccess(isUpdate ? 'Politique MFA rôle mise à jour avec succès.' : 'Politique MFA rôle créée avec succès.');
        onSaved?.(); loadRolePolicies();
      } else {
        notifyError(parsed?.Message ?? "Erreur lors de l'enregistrement.");
      }
    } catch (e: any) {
      const errorMsg = logMfaError('saveRolePolicy', '')?.Message ?? e?.message ?? "Erreur lors de l'enregistrement de la politique MFA rôle.";
      notifyError(errorMsg);
    } finally { setLoading(false); }
  }

async function saveUserAssignment() {
  if (!selectedUser) { notifyWarning('Veuillez sélectionner un utilisateur.'); return; }
  setLoading(true);
  try {
    const utilisateur = utilisateurs.find((u) => u.CODUTI === selectedUser);
    const payload = {
      CodUti: selectedUser,
      CodGroup: utilisateur?.CODGROUP ?? '',
      MfaMethod: Number(userMethod), // On définit la méthode actuelle
      RequiredMfaMethod: Number(userMethod), // Et on l'impose via l'admin
      IsRequiredByAdmin: true,
      Statut: 'ACTIF',
      ModifiedBy: currentUser?.coduti ?? 'SYSTEM',
    };
    logMfaRequest('saveUserAssignment', mfaApi.userMfaSave, payload);
    const res = await axios.post(mfaApi.userMfaSave, encryptBody(payload), { headers, withCredentials: true });

    const parsed = logMfaSuccess('saveUserAssignment', res);
    if (normalizeStatusCode(parsed) < 300) {
      notifySuccess(parsed?.Message ?? 'MFA attribué à l\u2019utilisateur avec succès.');
      onSaved?.(); loadConfiguredUsers();
    } else {
      notifyError(parsed?.Message ?? 'Erreur.');
    }
  } catch (e: any) {
    const parsed = logMfaError('saveUserAssignment', '');
    notifyError(parsed?.Message ?? 'Le serveur a refusé la transaction.');
  } finally { setLoading(false); }
}

  async function loadUserTempPassword() {
    if (!selectedUser) { setSelectedUserTemp(null); return; }
    try {
      logMfaRequest('loadUserTempPassword', mfaApi.mfaTempPasswordGet(selectedUser));
      const res = await axios.get(mfaApi.mfaTempPasswordGet(selectedUser), { headers, withCredentials: true });
      const parsed = logMfaSuccess('loadUserTempPassword', res);
      const result = unwrapMfaPayload(parsed);
      setSelectedUserTemp(result ?? null);
    } catch (e) {
      setSelectedUserTemp(null);
      logMfaError('loadUserTempPassword', '');
    }
  }

  async function handleSetTempPassword() {
    if (!selectedUser) { notifyWarning('Veuillez sélectionner un utilisateur.'); return; }
    if (!tempPassword || tempPassword.length < globalPolicy.PasswordMinLength) {
      notifyWarning(`Le mot de passe temporaire doit faire au moins ${globalPolicy.PasswordMinLength} caractères.`);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        CodUti: selectedUser,
        PlainPassword: tempPassword,
        CreatedBy: currentUser?.coduti ?? 'SYSTEM',
      };
      logMfaRequest('handleSetTempPassword', mfaApi.mfaTempPassword, payload);
      const res = await axios.post(mfaApi.mfaTempPassword, encryptBody(payload), { headers, withCredentials: true });
      const parsed = logMfaSuccess('handleSetTempPassword', res);
      if (normalizeStatusCode(parsed) < 300) {
        notifySuccess(`Mot de passe temporaire défini (expire le ${new Date(parsed?.Result?.ExpiresAt ?? parsed?.ExpiresAt).toLocaleString('fr-FR')}).`);
        setTempPassword('');
        loadUserTempPassword();
      } else {
        notifyError(parsed?.Message ?? 'Erreur lors de la définition du mot de passe temporaire.');
      }
    } catch (e: any) {
      const parsed = logMfaError('handleSetTempPassword', '');
      notifyError(parsed?.Message ?? 'Le serveur a refusé la transaction.');
    } finally { setLoading(false); }
  }

  useEffect(() => { loadUserTempPassword(); }, [selectedUser]);

  // ---------------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------------

  return (
    <Spin spinning={loading} size="large">
      <div className="tabItem" style={{ display: 'block' }}>
        <Box fontStyle="inherit" className="border" width="85%" position="absolute" style={{ padding: 10 }}>
          <legend><Typography color="#116599">Administration MFA</Typography></legend>
          <Typography variant="body2" sx={{ color: '#5d6b78', mb: 1 }}>
            Configurez le MFA à chaque niveau : sécurité globale, société, agence, groupe / rôle, ou utilisateur.
          </Typography>
          <AntTabs
            size="large"
            type="card"
            activeKey={level}
            onChange={(value) => setLevel(value as MfaLevel)}
            items={LEVELS.map((l) => ({ key: l.value, label: l.label }))}
          />
          <>
          {level === 'global' && (
            <>
              <fieldset className="bordure1">
              <legend><Typography color="#116599">Politique de sécurité globale</Typography></legend>
              <AntForm component="div" labelAlign="left" disabled={formLocked}>
                <Grid container spacing={0}>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item className="bordure" label="Longueur OTP" labelCol={LABEL_COL}>
                      <InputNumber min={4} max={8} style={{ width: '100%' }} value={globalPolicy.OtpLength}
                        onChange={(value) => setGlobalPolicy({ ...globalPolicy, OtpLength: Number(value) })} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item className="bordure" label="Mot de passe minimum" labelCol={LABEL_COL}>
                      <InputNumber min={6} max={128} style={{ width: '100%' }} value={globalPolicy.PasswordMinLength}
                        onChange={(value) => setGlobalPolicy({ ...globalPolicy, PasswordMinLength: Number(value) })} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item className="bordure" label="Mot de passe maximum" labelCol={LABEL_COL}>
                      <InputNumber min={6} max={128} style={{ width: '100%' }} value={globalPolicy.PasswordMaxLength}
                        onChange={(value) => setGlobalPolicy({ ...globalPolicy, PasswordMaxLength: Number(value) })} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item className="bordure" label="Statut" labelCol={LABEL_COL}>
                      <AntSelect style={{ width: '100%' }} value={globalPolicy.Statut}
                        onChange={(value) => setGlobalPolicy({ ...globalPolicy, Statut: value })}
                        options={STATUT_OPTIONS} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item className="bordure" label="Durée OTP (min)" labelCol={LABEL_COL}>
                      <InputNumber min={1} max={1440} style={{ width: '100%' }} value={globalPolicy.OtpLifetime}
                        onChange={(value) => setGlobalPolicy({ ...globalPolicy, OtpLifetime: Number(value) })} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item className="bordure" label="Tentatives OTP" labelCol={LABEL_COL}>
                      <InputNumber min={1} max={20} style={{ width: '100%' }} value={globalPolicy.OtpMaxAttempts}
                        onChange={(value) => setGlobalPolicy({ ...globalPolicy, OtpMaxAttempts: Number(value) })} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item className="bordure" label="Historique mots de passe" labelCol={LABEL_COL}>
                      <InputNumber min={0} max={50} style={{ width: '100%' }} value={globalPolicy.PasswordHistoryCount}
                        onChange={(value) => setGlobalPolicy({ ...globalPolicy, PasswordHistoryCount: Number(value) })} />
                    </AntForm.Item>
                  </Grid>

                </Grid>
              </AntForm>
              </fieldset>
              <MfaPasswordRotationPanel formLocked={formLocked} headers={headers} currentUser={currentUser} saveRequest={saveRequest} />
            </>
          )}

          {level === 'society' && (
            <fieldset className="bordure1">
              <legend><Typography color="#116599">Politique MFA société</Typography></legend>
              <Typography variant="body2" sx={{ color: '#5d6b78', mb: 1 }}>Configuration de la politique MFA globale pour toute la société.</Typography>
              <AntForm component="div" labelAlign="left" disabled={formLocked}>
                <Grid container spacing={0}>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item className="bordure" label="Méthode MFA par défaut" labelCol={LABEL_COL}>
                      <AntSelect style={{ width: '100%' }} value={Number(societyPolicy.DefaultMfaMethod ?? MFA_METHOD.emailOtp)}
                        onChange={(value) => setSocietyPolicy({ ...societyPolicy, DefaultMfaMethod: Number(value) })}
                        options={METHOD_OPTIONS_NO_NONE} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item className="bordure" label="Exigence MFA" labelCol={LABEL_COL}>
                      <Chip label={societyPolicy.IsRequired ? 'Obligatoire' : 'Optionnel'} color={societyPolicy.IsRequired ? 'success' : 'default'} sx={{ fontWeight: 700 }}
                        onClick={() => !formLocked && setSocietyPolicy({ ...societyPolicy, IsRequired: !societyPolicy.IsRequired })} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item className="bordure" label="Statut" labelCol={LABEL_COL}>
                      <AntSelect style={{ width: '100%' }} value={societyPolicy.Statut ?? 'ACTIF'}
                        onChange={(value) => setSocietyPolicy({ ...societyPolicy, Statut: value })}
                        options={STATUT_OPTIONS} />
                    </AntForm.Item>
                  </Grid>
                </Grid>
              </AntForm>
            </fieldset>
          )}

          {level === 'agency' && (
            <>
              <fieldset className="bordure1">
                <legend><Typography color="#116599">Politique MFA par agence</Typography></legend>
                <Typography variant="body2" sx={{ color: '#5d6b78', mb: 1 }}>Configuration spécifique par agence.</Typography>
                <AntForm component="div" labelAlign="left" disabled={formLocked}>
                  <Grid container spacing={0}>
                    <Grid item xs={12} sm={6} md={6}>
                      <AntForm.Item className="bordure" label="Agence" labelCol={LABEL_COL}>
                        <AntSelect showSearch optionFilterProp="label" placeholder="Sélectionner une agence" style={{ width: '100%' }}
                          value={selectedAgence || undefined}
                          onChange={(value) => setSelectedAgence(value ?? '')}
                          options={agences.map((agence) => ({ value: agence.CodeAgence, label: `${agence.CodeAgence} - ${agence.Libelle}` }))} />
                      </AntForm.Item>
                    </Grid>
                    {selectedAgence && (
                      <Grid item xs={12} sm={6} md={6}>
                        <AntForm.Item className="bordure" label="Méthode MFA" labelCol={LABEL_COL}>
                          <AntSelect style={{ width: '100%' }} value={Number(currentAgencePolicy.MfaMethod ?? MFA_METHOD.emailOtp)}
                            onChange={(value) => setCurrentAgencePolicy({ ...currentAgencePolicy, MfaMethod: Number(value) })}
                            options={METHOD_OPTIONS} />
                        </AntForm.Item>
                      </Grid>
                    )}
                  </Grid>
                </AntForm>
              </fieldset>

              <fieldset className="bordure1">
                <legend><Typography color="#116599">Politiques MFA par agence</Typography></legend>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--lightColor001)' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Agence</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Méthode</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agencePolicies.map((policy) => (
                      <TableRow key={policy.Id} hover selected={selectedAgence === policy.CodAge}
                        onClick={() => setSelectedAgence(policy.CodAge ?? '')} sx={{ cursor: 'pointer' }}>
                        <TableCell>{policy.CodAge}</TableCell>
                        <TableCell>{getMethodLabel(policy.MfaMethod)}</TableCell>
                        <TableCell><Chip label={policy.Statut === 'ACTIF' ? 'Actif' : 'Inactif'} color={policy.Statut === 'ACTIF' ? 'success' : 'default'} size="small" /></TableCell>
                      </TableRow>
                    ))}
                    {agencePolicies.length === 0 && (
                      <TableRow><TableCell colSpan={3} align="center">Aucune politique MFA agence configurée</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </fieldset>
            </>
          )}

          {level === 'role' && (
            <>
              <fieldset className="bordure1">
                <legend><Typography color="#116599">Politique MFA par groupe / rôle</Typography></legend>
                <Typography variant="body2" sx={{ color: '#5d6b78', mb: 1 }}>Configuration spécifique par groupe d'utilisateurs.</Typography>
                <AntForm component="div" labelAlign="left" disabled={formLocked}>
                  <Grid container spacing={0}>
                    <Grid item xs={12} sm={6} md={4}>
                      <AntForm.Item className="bordure" label="Groupe / rôle" labelCol={LABEL_COL}>
                        <AntSelect showSearch optionFilterProp="label" placeholder="Sélectionner un groupe" style={{ width: '100%' }}
                          value={selectedGroup || undefined}
                          onChange={(value) => setSelectedGroup(value ?? '')}
                          options={groups.map((group) => ({ value: group.CODGROUP, label: `${group.CODGROUP} - ${group.LIBELLE}` }))} />
                      </AntForm.Item>
                    </Grid>
                    {selectedGroup && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <AntForm.Item className="bordure" label="Méthode MFA" labelCol={LABEL_COL}>
                            <AntSelect style={{ width: '100%' }} value={Number(currentRolePolicy.MfaMethod ?? MFA_METHOD.emailOtp)}
                              onChange={(value) => setCurrentRolePolicy({ ...currentRolePolicy, MfaMethod: Number(value) })}
                              options={METHOD_OPTIONS} />
                          </AntForm.Item>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <AntForm.Item className="bordure" label="Statut" labelCol={LABEL_COL}>
                            <AntSelect style={{ width: '100%' }} value={currentRolePolicy.Statut ?? 'ACTIF'}
                              onChange={(value) => setCurrentRolePolicy({ ...currentRolePolicy, Statut: value })}
                              options={STATUT_OPTIONS} />
                          </AntForm.Item>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </AntForm>
              </fieldset>

              <fieldset className="bordure1">
                <legend><Typography color="#116599">Politiques MFA par groupe</Typography></legend>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--lightColor001)' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Groupe</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Méthode</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rolePolicies.map((policy) => (
                      <TableRow key={policy.CodGroup} hover selected={selectedGroup === policy.CodGroup}
                        onClick={() => setSelectedGroup(policy.CodGroup ?? '')} sx={{ cursor: 'pointer' }}>
                        <TableCell>{policy.CodGroup}</TableCell>
                        <TableCell>{getMethodLabel(policy.MfaMethod)}</TableCell>
                        <TableCell><Chip label={policy.Statut === 'ACTIF' ? 'Actif' : 'Inactif'} color={policy.Statut === 'ACTIF' ? 'success' : 'default'} size="small" /></TableCell>
                      </TableRow>
                    ))}
                    {rolePolicies.length === 0 && (
                      <TableRow><TableCell colSpan={3} align="center">Aucune politique MFA groupe configurée</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </fieldset>
            </>
          )}

          {level === 'user' && (
            <>
              <fieldset className="bordure1">
                <legend><Typography color="#116599">Attribution MFA par utilisateur</Typography></legend>
                <Typography variant="body2" sx={{ color: '#5d6b78', mb: 1 }}>Configuration spécifique à un utilisateur, prioritaire sur les autres niveaux.</Typography>
                <AntForm component="div" labelAlign="left" disabled={formLocked}>
                  <Grid container spacing={0}>
                    <Grid item xs={12} sm={6} md={6}>
                      <AntForm.Item className="bordure" label="Utilisateur" labelCol={LABEL_COL}>
                        <AntSelect showSearch optionFilterProp="label" placeholder="-- Choisir un utilisateur --" style={{ width: '100%' }}
                          value={selectedUser || undefined}
                          onChange={(value) => setSelectedUser(value ?? '')}
                          options={utilisateurs.map((util) => ({ value: util.CODUTI, label: `${util.CODUTI} - ${util.NOMUTI} (${util.CODAGE})` }))} />
                      </AntForm.Item>
                    </Grid>
                    {selectedUser && (
                      <Grid item xs={12} sm={6} md={6}>
                        <AntForm.Item className="bordure" label="Méthode MFA" labelCol={LABEL_COL}>
                          <AntSelect style={{ width: '100%' }} value={userMethod}
                            onChange={(value) => setUserMethod(Number(value))}
                            options={METHOD_OPTIONS} />
                        </AntForm.Item>
                      </Grid>
                    )}
                  </Grid>
                </AntForm>
              </fieldset>



              <fieldset className="bordure1">
                <legend><Typography color="#116599">Utilisateurs MFA ({configuredUsers.length})</Typography></legend>
                {configuredUsers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">Aucun utilisateur MFA configuré.</Typography>
                  </Box>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'var(--lightColor001)' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Utilisateur</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Groupe</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Méthode</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Statut</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#116599' }}>Activation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {configuredUsers.map((u) => (
                        <TableRow key={u.CODUTI} hover selected={selectedUser === u.CODUTI}
                          onClick={() => { setSelectedUser(u.CODUTI ?? ''); setUserMethod(Number(u.MFAMETHOD ?? MFA_METHOD.totp)); }} sx={{ cursor: 'pointer' }}>
                          <TableCell><strong>{u.CODUTI}</strong></TableCell>
                          <TableCell>{u.CODGROUP ?? '—'}</TableCell>
                          <TableCell>{getMethodLabel(u.MFAMETHOD)}</TableCell>
                          <TableCell>{u.STATUT ?? 'ACTIF'}</TableCell>
                          <TableCell>{u.LASTCONFIGUREDDATE ? new Date(u.LASTCONFIGUREDDATE).toLocaleString('fr-FR') : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </fieldset>
            </>
          )}

          </>
        </Box>
      </div>
    </Spin>
  );
}